import { map, Observable, switchMap } from 'rxjs';
import { Pub, Service, Sub } from '../../../../shared/services/service.abstract';
import { gqlClient } from '../../../../graphql/graphqlRequest.ts';
import {
  getReportsConfiguration,
  saveReportsConfiguration,
  getDataForReportsConfigurationPreview,
  listReportPrinterMappings,
  saveReportPrinterMappings,
} from './gql/printForms.gql.ts';
import {
  GetReportsConfigurationQuery,
  SaveReportsConfigurationMutation,
  SaveReportsConfigurationMutationVariables,
  GetWa_DataForReportsConfigurationPreviewQuery,
  SaveReportPrinterMappingsMutationVariables,
  ListReportPrinterMappingsQuery,
  SaveReportPrinterMappingsMutation,
} from '../../../../graphql/generatedModel.ts';

type Actions =
  | 'getReportsConfiguration'
  | 'saveReportsConfiguration'
  | 'getDataForReportsConfigurationPreview'
  | 'listReportPrinterMappings'
  | 'saveReportPrinterMappings';

class PubImpl extends Pub<Actions> {
  getReportsConfiguration(params?: { subCompanyId?: string }) {
    this.emit('getReportsConfiguration', params);
  }
  saveReportsConfiguration(params: SaveReportsConfigurationMutationVariables): void {
    this.emit('saveReportsConfiguration', params);
  }
  getDataForReportsConfigurationPreview() {
    this.emit('getDataForReportsConfigurationPreview');
  }
  listReportPrinterMappings() {
    this.emit('listReportPrinterMappings');
  }
  saveReportPrinterMappings(params: SaveReportPrinterMappingsMutationVariables) {
    this.emit('saveReportPrinterMappings', params);
  }
}

class SubImpl extends Sub<Actions> {
  getReportsConfiguration(): Observable<ReportsConfigurationRes> {
    return this.actionListener('getReportsConfiguration').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          getReportsConfiguration,
          params,
        ) as Observable<GetReportsConfigurationQuery>;
      }),
      map((data: GetReportsConfigurationQuery) => {
        return data.wawiAssist?.getReportsConfiguration as ReportsConfigurationRes;
      }),
    );
  }
  saveReportsConfiguration(): Observable<SaveReportsConfigurationRes> {
    return this.actionListener('saveReportsConfiguration').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          saveReportsConfiguration,
          params,
        ) as Observable<SaveReportsConfigurationMutation>;
      }),
      map((data) => {
        return data.wawiAssist?.saveReportsConfiguration as SaveReportsConfigurationRes;
      }),
    );
  }
  getDataForReportsConfigurationPreview(): Observable<DataForReportsConfigurationPreviewRes> {
    return this.actionListener('getDataForReportsConfigurationPreview').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          getDataForReportsConfigurationPreview,
          params,
        ) as Observable<GetWa_DataForReportsConfigurationPreviewQuery>;
      }),
      map((data: GetWa_DataForReportsConfigurationPreviewQuery) => {
        return data.wawiAssist
          ?.getWA_DataForReportsConfigurationPreview as DataForReportsConfigurationPreviewRes;
      }),
    );
  }
  listReportPrinterMappings(): Observable<ListReportPrinterMappingsQueryRes> {
    return this.actionListener('listReportPrinterMappings').pipe(
      switchMap(() => {
        return gqlClient(listReportPrinterMappings) as Observable<ListReportPrinterMappingsQuery>;
      }),
      map((data: ListReportPrinterMappingsQuery) => {
        return data.wawiAssist?.listWA_ReportPrinterMappings as ListReportPrinterMappingsQueryRes;
      }),
    );
  }
  saveReportPrinterMappings(): Observable<SaveReportPrinterMappingsMutationRes> {
    return this.actionListener('saveReportPrinterMappings').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          saveReportPrinterMappings,
          params,
        ) as Observable<SaveReportPrinterMappingsMutation>;
      }),
      map((data: SaveReportPrinterMappingsMutation) => {
        return data.wawiAssist
          ?.saveWA_ReportPrinterMappings as SaveReportPrinterMappingsMutationRes;
      }),
    );
  }
}

class PrintFormsService extends Service<Actions> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const printFormsService = new PrintFormsService();

export type ReportsConfigurationRes = NonNullable<
  NonNullable<GetReportsConfigurationQuery['wawiAssist']>['getReportsConfiguration']
>;

export type SaveReportsConfigurationRes = NonNullable<
  NonNullable<
    NonNullable<SaveReportsConfigurationMutation['wawiAssist']>['saveReportsConfiguration']
  >
>;

export type DataForReportsConfigurationPreviewRes = NonNullable<
  NonNullable<
    GetWa_DataForReportsConfigurationPreviewQuery['wawiAssist']
  >['getWA_DataForReportsConfigurationPreview']
>;

export type ListReportPrinterMappingsQueryRes = NonNullable<
  NonNullable<ListReportPrinterMappingsQuery['wawiAssist']>['listWA_ReportPrinterMappings']
>;
export type SaveReportPrinterMappingsMutationRes = NonNullable<
  NonNullable<SaveReportPrinterMappingsMutation['wawiAssist']>['saveWA_ReportPrinterMappings']
>;
